import { useState } from "react";
import { useQuery } from "wasp/client/operations";
import { getRecentPayoutsForUser } from "wasp/client/operations";
import { Card, CardContent } from "../client/components/ui/card";
import { CardSectionHeader } from "../client/components/ui/card-section-header";
import { Button } from "../client/components/ui/button";
import { Badge } from "../client/components/ui/badge";
import { Plus, CheckCircle2, Pencil } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { AttachmentUploadDialog } from "../account-attachments/components/AttachmentUploadDialog";
import EditPayoutDialog, { type EditablePayout } from "../trading-accounts/EditPayoutDialog";

export function PayoutsListSection() {
  const t = useTranslation();
  const { data: payouts, refetch } = useQuery(getRecentPayoutsForUser, { limit: 30 });
  const [uploadTarget, setUploadTarget] = useState<{
    accountId: string;
    payoutId: string;
  } | null>(null);
  const [editingPayout, setEditingPayout] = useState<EditablePayout | null>(null);

  function fmt(amount: number, currency: string) {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  }

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader label={t.propFirmManager.recentPayoutsTitle} isDark={false} compact />
      <CardContent className="flex-1 overflow-auto pt-3">
        {!payouts || payouts.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t.propFirmManager.recentPayoutsEmpty}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-muted-foreground border-b border-border">
                  <th className="text-left pb-2 pr-3 font-medium">
                    {t.propFirmManager.recentPayoutsHeaderDate}
                  </th>
                  <th className="text-left pb-2 pr-3 font-medium">
                    {t.propFirmManager.recentPayoutsHeaderAccount}
                  </th>
                  <th className="text-right pb-2 pr-3 font-medium">
                    {t.propFirmManager.recentPayoutsHeaderAmount}
                  </th>
                  <th className="text-right pb-2 font-medium">
                    {t.propFirmManager.recentPayoutsHeaderActions}
                  </th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id} className="border-b border-border">
                    <td className="py-2.5 pr-3 font-mono">{new Date(p.date).toLocaleDateString()}</td>
                    <td
                      className="py-2.5 pr-3 truncate max-w-[200px]"
                      title={p.accountName}
                    >
                      {p.accountName}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono">
                      <span className="block text-pnlPositive">+{fmt(p.amount, p.accountCurrency)}</span>
                      {p.grossAmount != null && (
                        <span className="block text-[10px] text-muted-foreground">
                          {t.tradingAccounts.payoutGrossShort.replace("{amount}", fmt(p.grossAmount, p.accountCurrency))}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => setEditingPayout(p)}
                        aria-label={t.tradingAccounts.payoutEdit}
                        title={t.tradingAccounts.payoutEdit}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {p.attachmentId ? (
                        <Badge
                          variant="outline"
                          className="border-primary/40 text-primary gap-1"
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          {t.attachments.certificateBadge}
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setUploadTarget({ accountId: p.accountId, payoutId: p.id })
                          }
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          {t.attachments.addCertificate}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <EditPayoutDialog payout={editingPayout} onClose={() => setEditingPayout(null)} onSaved={refetch} />

      {uploadTarget && (
        <AttachmentUploadDialog
          open={true}
          onOpenChange={(open) => {
            if (!open) setUploadTarget(null);
          }}
          accountId={uploadTarget.accountId}
          initialPayoutId={uploadTarget.payoutId}
          initialType="payout"
          onSuccess={() => {
            refetch();
            setUploadTarget(null);
          }}
        />
      )}
    </Card>
  );
}
